import * as React from 'react';
import { goBack } from 'react-router-redux';
import { connect } from 'react-redux';
import { range, merge } from 'lodash';
import moment = require('moment');
import { getCompany, mergeCompanies } from '../../redux/actions/companies';
import {
  Card,
  IconButton,
  Notification,
  MergeForm,
  RouteWrapper,
  HeadingSection,
  Section,
} from '../../components';

interface Props {
  match: any;
  goBack: () => void;
  mergeCompanies: (companyIds: Array<number>, overrides?) => Promise<any>;
  getCompany: (id: number) => Promise<any>;
}

interface State {
  id: number;
  mergeCompanyId: number;
  isFetching: boolean;
  companies: Array<any>;
  selectedFields: number[];
  error: string | boolean;
}

class Route extends React.Component<Props, State> {
  state = {
    companies: [],
    isFetching: true,
    id: Number(this.props.match.params.id),
    mergeCompanyId: Number(this.props.match.params.mergeCompanyId),
    selectedFields: range(0, Route.FIELDS.length, 0),
    error: false,
  };

  private static CUSTOM_LABELS = {
    pay_own_insurance: 'pays own insurance?',
  };

  private static FIELDS: string[] = [
    'name',
    'phone_number',
    'postcode',
    'address',
    'address_2',
    'city',
    'county',
    'general_contact_name',
    'general_contact_email',
    'accounts_contact_name',
    'accounts_contact_email',
    'pay_own_insurance',
    'insurer',
    'policy_number',
    'expiry_date',
    'has_account',
    'account_type',
    'account_card_expiry',
    'account_online_bypass',
  ];

  componentDidMount() {
    this.getCompanies();
  }

  private async getCompanies() {
    const companies = await Promise.all([
      this.props.getCompany(this.state.id),
      this.props.getCompany(this.state.mergeCompanyId),
    ]);

    if (
      companies.filter((company) => company.error || company.data.deleted_at)
        .length
    ) {
      this.setState({
        error: 'The companies selected do not exist',
      });
    } else {
      this.setState({
        isFetching: false,
        companies: companies.map(({ data }) => data),
      });
    }
  }

  private setSelectedField = (index, companyIndex) => {
    const selectedFields = [...this.state.selectedFields];
    selectedFields[index] = companyIndex;
    this.setState({
      selectedFields,
    });
  };

  private getYesNo = (value) => {
    return value ? 'Yes' : 'No';
  };

  private selectAll = (companyIndex) => {
    this.setState({
      selectedFields: range(
        companyIndex,
        companyIndex + Route.FIELDS.length,
        0,
      ),
    });
  };

  private changeFormValues = (company) => {
    if (!company) {
      return {};
    }

    return merge({}, company, {
      pay_own_insurance: this.getYesNo(company.pay_own_insurance),
      expiry_date: moment(company.expiry_date).calendar(),
      has_account: this.getYesNo(company.has_account),
      account_card_expiry: moment(company.account_card_expiry).calendar(),
      account_online_bypass: this.getYesNo(company.account_online_bypass),
    });
  };

  private get mergedValues() {
    const merged = {};
    Route.FIELDS.forEach((field, index) => {
      const companyIndex = this.state.selectedFields[index];
      const value = this.state.companies[companyIndex][field];
      if (value !== null) {
        merged[field] = value;
      }
    });
    return merged;
  }

  private merge = async () => {
    if (
      !window.confirm('Are you sure you wish to merge these two companies?')
    ) {
      return;
    }

    const { error } = await this.props.mergeCompanies(
      [this.state.id, this.state.mergeCompanyId],
      this.mergedValues,
    );

    if (error) {
      this.setState({ error });
    } else {
      this.props.goBack();
    }
  };

  render() {
    return (
      <RouteWrapper
        requireAuth
        requirePermission="MANAGE_COMPANIES"
        title="Merge Companies"
        ready={!this.state.isFetching}
        error={this.state.isFetching && this.state.error}
      >
        <HeadingSection
          title="Merge Companies"
          controls={[
            <IconButton
              key="return"
              icon="undo"
              className="is-default is-pulled-right"
              handleClick={() => {
                this.props.goBack();
              }}
            >
              Return
            </IconButton>,
          ]}
        />
        <Section>
          <div className="columns">
            {[0, 1].map((companyIndex) => (
              <div className="column is-6" key={companyIndex}>
                <div className="level">
                  <div className="level-left">
                    <div className="level-item">
                      <strong>
                        {this.state.companies[companyIndex] &&
                          this.state.companies[companyIndex].name}
                      </strong>
                    </div>
                  </div>
                  <div className="level-right">
                    <div className="level-item">
                      <button
                        className="is-default"
                        onClick={() => this.selectAll(companyIndex)}
                      >
                        Select All
                      </button>
                    </div>
                  </div>
                </div>
                <MergeForm
                  fields={Route.FIELDS}
                  customLabels={Route.CUSTOM_LABELS}
                  values={this.changeFormValues(
                    this.state.companies[companyIndex],
                  )}
                  select={(index: number) => {
                    this.setSelectedField(index, companyIndex);
                  }}
                  isSelected={(index: number) => {
                    return this.state.selectedFields[index] === companyIndex;
                  }}
                />
              </div>
            ))}
          </div>
          {this.state.error && (
            <Notification type="danger">{this.state.error}</Notification>
          )}
          <div className="buttons is-right">
            <button className="is-primary" onClick={this.merge}>
              Merge
            </button>
          </div>
        </Section>
      </RouteWrapper>
    );
  }
}

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  getCompany(companyId: number) {
    return dispatch(getCompany(companyId));
  },
  goBack() {
    return dispatch(goBack());
  },
  mergeCompanies(companyIds, overrides) {
    return dispatch(mergeCompanies(companyIds, overrides));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Route);
